/**
 * Mastery rules for a vocabulary word — derived from the last 3 answer attempts.
 *
 * Display rules (see design mock):
 * - Attempts 1~3 always show `learning` (習得中), no matter how many were correct.
 * - From attempt 4 on, the level comes from the 3 most recent results:
 *   3 correct → `strong` (得意), 2 → `strongish` (やや得意), 1 → `weakish` (やや苦手), 0 → `weak` (苦手).
 * - `masteryCorrectCount >= 2` earns the ✓ badge. The count never decreases, so once
 *   shown the badge stays even if the learner later drops to `weak`.
 */

import { TVocabularyAnswerResult, TVocabularyMasteryLevel } from "@/infrastructure/adapters/vocabulary/vocabulary.types";

/** Number of attempt markers rendered in a row (★ / ☆ filled by results, ・ for the rest) */
export const RECENT_RESULTS_SLOT_COUNT = 3;

/** Correct answers required for the permanent ✓ (習得済み) badge */
export const MASTERED_CORRECT_THRESHOLD = 2;

/** BE flag for a correct answer inside the `recentResults` string */
const CORRECT_FLAG = "1";

/**
 * Normalize the BE `recentResults` string into a domain array.
 * BE sends one flag per attempt — "1" correct, "0" wrong — leftmost oldest, rightmost newest.
 * Keeps only the newest `RECENT_RESULTS_SLOT_COUNT` flags; a missing value yields an empty array.
 */
export const parseRecentResults = (raw: string | null | undefined): TVocabularyAnswerResult[] =>
  Array.from(raw ?? "")
    .slice(-RECENT_RESULTS_SLOT_COUNT)
    .map((flag) => (flag === CORRECT_FLAG ? "correct" : "wrong"));

/** Whether the word has earned the permanent ✓ (習得済み) badge */
export const isMasteredByCorrectCount = (masteryCorrectCount: number): boolean => masteryCorrectCount >= MASTERED_CORRECT_THRESHOLD;

/** Derive the mastery level label shown above the word */
export const getMasteryLevel = (recentResults: TVocabularyAnswerResult[]): TVocabularyMasteryLevel => {
  // Still inside the first 3 attempts — always 習得中
  if (recentResults.length < RECENT_RESULTS_SLOT_COUNT) return "learning";

  const correctCount = recentResults.filter((result) => result === "correct").length;
  if (correctCount === 3) return "strong";
  if (correctCount === 2) return "strongish";
  if (correctCount === 1) return "weakish";
  return "weak";
};
